import { differenceInHours } from 'date-fns';
import { Record, List } from 'immutable';
import _ from 'lodash';

import User from 'entities/user/model';

export enum DurationUnit {
  hours = 'hours',
  days = 'days',
  weeks = 'weeks',
  months = 'months',
}

interface DurationInformation {
  min: number;
  max: number;
  unit: DurationUnit;
}

const properties = {
  id: '',
  name: '',
  autoApproval: false,
  description: undefined,
  requiredAmount: 0,
  startingAt: null,
  endingAt: null,
  durationMin: 0,
  durationMax: 0,
  estimatedHoursMin: 0,
  estimatedHoursMax: 0,
  estimatedValue: 0,
  durationUnit: DurationUnit.weeks,
  applicants: [],
  approved: [],
  rejected: [],
  skills: [],
  mode: undefined,
};

const calculateDurationInformation = function (this: DeedRole): DurationInformation {
  // @NOTE-RT: This assumption is true until we decide otherwise. And when that happens, we can start enforcing `durationUnit` as required on the `app` side...
  if (this.startingAt && this.endingAt) {
    const difference = differenceInHours(this.endingAt, this.startingAt);

    return {
      min: difference,
      max: difference,
      unit: DurationUnit.hours,
    };
  }

  return {
    min: this.get('durationMin') as number,
    max: this.get('durationMax') as number,
    unit: (this.get('durationUnit') as DurationUnit) || DurationUnit.weeks,
  };
};

const calculateDurationField = (field: keyof DurationInformation) =>
  function (this: DeedRole) {
    return calculateDurationInformation.call(this)[field];
  };

const calculateEstimatedHoursField = (field: keyof Omit<DurationInformation, 'unit'>) =>
  function (this: DeedRole) {
    const calculatedDuration = calculateDurationInformation.call(this);

    if (calculatedDuration.unit === DurationUnit.hours) {
      return calculatedDuration[field];
    }

    switch (field) {
      case 'min':
        return this.get('estimatedHoursMin') as number;

      case 'max':
        return this.get('estimatedHoursMax') as number;

      default:
        throw new Error(`Unrecognized field:${field}`);
    }
  };

export class DeedRole extends Record(properties, 'DeedRole') {
  public readonly id!: string;

  public readonly name!: string;

  public readonly description!: string;

  public readonly autoApproval!: boolean;

  public readonly requiredAmount!: number;

  public readonly startingAt!: Date;

  public readonly endingAt!: Date;

  public readonly estimatedValue!: number;

  public readonly applicants!: List<User>;

  public readonly approved!: List<User>;

  public readonly rejected!: List<User>;

  public readonly skills!: List<string>;

  public readonly mode!: string;

  constructor(values: any = {}) {
    const validValues = _.pick(values, Object.keys(properties));

    super({
      ...validValues,
      startingAt: values.startingAt && new Date(values.startingAt),
      endingAt: values.endingAt && new Date(values.endingAt),
      applicants:
        values.applicants &&
        List(values.applicants.map((applicant: Record<string, unknown>) => new User(applicant))),
      approved:
        values.approved &&
        List(values.approved.map((approved: Record<string, unknown>) => new User(approved))),
      rejected:
        values.rejected &&
        List(values.rejected.map((rejected: Record<string, unknown>) => new User(rejected))),
      skills: values.skills && List(values.skills),
    });
  }

  get durationUnit() {
    return calculateDurationField('unit').call(this);
  }

  get durationMin() {
    return calculateDurationField('min').call(this);
  }

  get durationMax() {
    return calculateDurationField('max').call(this);
  }

  get estimatedHoursMin() {
    return calculateEstimatedHoursField('min').call(this);
  }

  get estimatedHoursMax() {
    return calculateEstimatedHoursField('max').call(this);
  }
}

export default DeedRole;
